/* ──────────────────────────────────────────────
   RESULTS SCREEN DATA — 3 Sections
   ────────────────────────────────────────────── */

export const SECTION_COLOURS = ['#3b82f6', '#7c3aed', '#f59e0b'];
export const SECTION_PILL_BG = ['#dbeafe', '#ede9fe', '#fef3c7'];

export const SECTION_NAMES_FULL = [
  'Customer Intelligence',
  'Communication Execution',
  'Omni-Channel Loyalty & Offers',
];

export const SECTION_QUESTIONS = {
  0: [0, 1, 2],
  1: [3, 4, 5, 6],
  2: [7, 8],
};

export const SECTION_GRID_COLS = [3, 2, 2];
export const SECTION_FINAL_WEIGHTS = [0.3, 0.4, 0.3];
export const SECTION_MAX_UPLIFT = [5, 7, 7];

export const SECTION_DESCRIPTIONS = {
  0: "How deeply you understand, unify, and predict your customers.",
  1: "How automated, personalised, and measurable your communications are.",
  2: "The sophistication of your loyalty programme and cross-channel offer management.",
};

/* Grade color system */
export const GRADE_CONFIG = {
  A: { color: '#EF4444', light: '#FEF2F2', text: '#B91C1C', label: 'Needs Improvement' },
  B: { color: '#F97316', light: '#FFF7ED', text: '#C2410C', label: 'Basic' },
  C: { color: '#F59E0B', light: '#FFFBEB', text: '#92400E', label: 'Developing' },
  D: { color: '#10B981', light: '#ECFDF5', text: '#065F46', label: 'Best in Class' },
};

export const XENO_UPGRADE = {
  0: { heading: "Complete 360° View", description: "Xeno unifies every touchpoint — POS, e-commerce, app, support, and NPS — into a single customer timeline." },
  1: { heading: "AI Insights Agent & Opportunity Finder", description: "Xeno surfaces cross-dimensional insights automatically, so your team receives actionable intelligence." },
  2: { heading: "Predictive AI Model Creator", description: "Xeno's predictive suite — churn, LTV, next purchase, product affinity — fires automated journeys when scores cross thresholds." },
  3: { heading: "AI Decisioning Agents", description: "Xeno runs intelligent omni-channel lifecycle journeys personalised for each customer with AI selecting the best action." },
  4: { heading: "AI Channel Intelligence", description: "Xeno's AI learns each customer's preferred channel and routes messages automatically to maximise engagement." },
  5: { heading: "Personalised Product Recommendations", description: "Xeno generates individual messages — content, product recommendations, and offer — per customer at send time." },
  6: { heading: "Communication Deep Dive Insights", description: "Xeno measures true incremental revenue using holdout groups at campaign and goal level." },
  7: { heading: "Rewards Marketplace & Gamification", description: "Xeno powers a full tiered loyalty program with every tier, earn rate, and benefit configurable in one place." },
  8: { heading: "Omni-Channel Personalised Offers", description: "Xeno's unified offer engine creates a promotion once and auto-deploys it to POS and e-commerce simultaneously." },
};

export const XENO_PITCH = {
  0: "Xeno's customer intelligence layer unifies every online and offline touchpoint into a single customer record and surfaces cross-dimensional insights automatically.",
  1: "Xeno orchestrates intelligent omni-channel lifecycle campaigns with AI selecting the right channel per customer, smart retry built in, and every campaign measured for true incrementality.",
  2: "Xeno powers the full loyalty and offer stack — from programme design and tier management through to a unified offer engine that deploys to POS and e-commerce simultaneously.",
};

const DIAGNOSIS = {
  0: {
    low: (c) => `${c} is working with a fragmented view of its customers. Without unified data and predictive models, CRM decisions are based on incomplete signals — limiting every downstream capability.`,
    mid: (c) => `${c} has a working view of customer health but is missing the diagnostic depth to act with precision. The gap between diagnostic analytics and AI-surfaced insights represents meaningful untapped revenue.`,
    high: (c) => `${c} has strong data capability but is a step away from proactive intelligence. The gap between asking questions and the system answering them automatically represents the next performance increment.`,
  },
  1: {
    low: (c) => `${c} is leaving significant reachable revenue on the table through manual campaign processes. Every campaign requiring human orchestration runs too slowly and with too little personalisation.`,
    mid: (c) => `${c} has automation in place but is missing multi-channel intelligence and trigger-based lifecycle journeys. Without holdout groups, claimed campaign revenue cannot be trusted.`,
    high: (c) => `${c} is executing well on communication fundamentals. The remaining gap is at the intelligence layer — AI-driven channel affinity, dynamic content, and reinforcement learning optimisation.`,
  },
  2: {
    low: (c) => `${c} does not yet have the retention mechanics to convert transactional shoppers into loyal customers. Without a loyalty programme and integrated offer management, every promotion requires double the effort.`,
    mid: (c) => `${c} has loyalty infrastructure but it is transactional rather than relational, and offer management requires manual coordination. The gap between a flat points programme and a tiered experiential programme is where improvement sits.`,
    high: (c) => `${c} has a strong loyalty programme but the integration between loyalty events and CRM automation isn't yet seamless. Collapsing the manual handoff is the remaining opportunity.`,
  },
};

export const getDiagnosis = (sectionIndex, score, companyName) => {
  const section = DIAGNOSIS[sectionIndex];
  if (!section) return '';
  const scorePct = score * 10;
  const band = scorePct < 40 ? 'low' : scorePct < 70 ? 'mid' : 'high';
  return section[band](companyName);
};
